import { GableTruss, HeaderTruss, LeanToTruss, TrussRequest } from '../../types';
import { postSizeDisplayToEnum, resolveBuildingPostSizeDisplay } from './building-constants';
import { resolveInsidePostMeasurementForTruss } from './truss-calculation';

/**
 * Format measurement for display with fractional inch rounding
 * Rounds to nearest quarter inch:
 * - < 0.125: Round down to whole inch
 * - 0.125 to < 0.375: Round to 1/4 inch
 * - 0.375 to < 0.625: Round to 1/2 inch
 * - 0.625 to < 0.875: Round to 3/4 inch
 * - >= 0.875: Round up to next whole inch
 *
 * Examples:
 * - 5.1 inches displays as "5"
 * - 5.25 inches displays as "5 1/4"
 * - 5.5 inches displays as "5 1/2"
 * - 5.8 inches displays as "5 3/4"
 * - 5.9 inches displays as "6"
 */
export const formatMeasurement = (totalInches: number): string => {
  const feet = Math.floor(totalInches / 12);
  const rawInches = totalInches % 12;

  // Round to nearest quarter inch
  const roundedInches = Math.round(rawInches * 4) / 4;

  // Handle whole inches
  if (roundedInches === 0) {
    return `${feet}'`;
  }

  // Check if it's a whole number of inches
  if (roundedInches === Math.floor(roundedInches)) {
    return `${feet}' ${Math.floor(roundedInches)}"`;
  }

  // Convert to fractional representation
  const wholeInches = Math.floor(roundedInches);
  const fractionalPart = roundedInches - wholeInches;

  let fraction: string;
  if (Math.abs(fractionalPart - 0.25) < 0.01) {
    fraction = '1/4';
  } else if (Math.abs(fractionalPart - 0.5) < 0.01) {
    fraction = '1/2';
  } else if (Math.abs(fractionalPart - 0.75) < 0.01) {
    fraction = '3/4';
  } else {
    // Fallback: round to nearest quarter
    const quarter = Math.round(fractionalPart * 4);
    if (quarter === 0) {
      return wholeInches > 0 ? `${feet}' ${wholeInches}"` : `${feet}'`;
    } else if (quarter === 1) {
      fraction = '1/4';
    } else if (quarter === 2) {
      fraction = '1/2';
    } else if (quarter === 3) {
      fraction = '3/4';
    } else {
      // Round up to next whole inch
      return wholeInches + 1 > 0 ? `${feet}' ${wholeInches + 1}"` : `${feet + 1}'`;
    }
  }

  if (wholeInches === 0) {
    return `${feet}' ${fraction}"`;
  }
  return `${feet}' ${wholeInches} ${fraction}"`;
};

/**
 * Normalize legacy backend classification strings to MVP display values.
 */
export const normalizeLegacyClassification = (classification: string): string => {
  if (!classification) return 'Standard';

  const legacyToDisplay: Record<string, string> = {
    StandardT1: 'Standard',
    StandardT2: 'Standard',
    FloridaT1: 'Standard',
    FloridaT2: 'Standard',
    LargeBayT1: 'Standard',
    LargeBayT2: 'Standard',
    CustomWork: 'Standard',
    MonoslopeT1: 'Monoslope',
    MonoslopeT2: 'Monoslope',
    Class1T1: 'Class 1',
    Class1T2: 'Class 1',
    Classification130T1: 'Class 1',
    Classification130T2: 'Class 1',
    Classification170T1: 'Class 1',
    Classification170T2: 'Class 1',
    Class1: 'Class 1', // backend enum name → price-sheet display label
    Standard: 'Standard',
    Monoslope: 'Monoslope',
  };

  return legacyToDisplay[classification] ?? classification;
};

/** Legacy backend enum values for 170 classification (distinct 6x6 span limit from Class 1). */
const CLASSIFICATION_170_VALUES = new Set([
  'Classification170T1',
  'Classification170T2',
  'Classification170',
  '170',
]);

export const isClassification170 = (classification: string): boolean =>
  CLASSIFICATION_170_VALUES.has(classification);

/**
 * Maximum truss span (in feet) permitted on 6x6 posts, keyed by structural
 * classification (display value). Spans beyond this require 8x8 posts.
 * This is a soft, frontend-only warning — the user may acknowledge and proceed.
 *  - Standard: 50' (legacy rule, UDOMI-784)
 *  - Class 1:  16'
 *  - 170:      40' (legacy enum; separate from Class 1 for 6x6 warnings)
 *  - Monoslope: 12'
 */
export const SIX_BY_SIX_MAX_SPAN_FEET: Record<string, number> = {
  Standard: 50,
  'Class 1': 16,
  '170': 40,
  Monoslope: 12,
};

/** Fallback 6x6 max span (feet) for unknown/blank classifications (e.g. header trusses). */
export const DEFAULT_SIX_BY_SIX_MAX_SPAN_FEET = 50;

/**
 * Label used in 6x6 post-size warning messages. 170 legacy enums keep their own
 * limit/text even though other rules map them to Class 1.
 */
export const getSixBySixWarningLabel = (classification: string): string => {
  if (isClassification170(classification)) return '170';
  return normalizeLegacyClassification(classification || 'Standard');
};

/**
 * Returns the maximum 6x6 span (feet) allowed for a classification, normalizing
 * legacy backend values first.
 */
export const getSixBySixMaxSpanFeet = (classification: string): number => {
  if (isClassification170(classification)) return SIX_BY_SIX_MAX_SPAN_FEET['170'];
  const normalized = normalizeLegacyClassification(classification || 'Standard');
  return SIX_BY_SIX_MAX_SPAN_FEET[normalized] ?? DEFAULT_SIX_BY_SIX_MAX_SPAN_FEET;
};

/**
 * Determines whether a truss span exceeds the 6x6 post limit for its classification.
 * Standard keeps the legacy inclusive rule (>= 50'); Class 1 / 170 / Monoslope warn strictly
 * above their max span ("over X' 0\"" per spec).
 */
export const exceedsSixBySixLimit = (classification: string, totalSpanFeet: number): boolean => {
  const maxSpan = getSixBySixMaxSpanFeet(classification);
  if (isClassification170(classification)) {
    return totalSpanFeet > maxSpan;
  }
  const normalized = normalizeLegacyClassification(classification || 'Standard');
  return normalized === 'Standard' ? totalSpanFeet >= maxSpan : totalSpanFeet > maxSpan;
};

/**
 * Builds the human-readable threshold phrase for the post-size warning message,
 * e.g. "50 feet or larger" (Standard, inclusive) or "over 16' 0\"" (Class 1 / Monoslope).
 */
export const sixBySixLimitText = (classification: string): string => {
  const maxSpan = getSixBySixMaxSpanFeet(classification);
  if (isClassification170(classification)) {
    return `over ${maxSpan}' 0"`;
  }
  const normalized = normalizeLegacyClassification(classification || 'Standard');
  return normalized === 'Standard' ? `${maxSpan} feet or larger` : `over ${maxSpan}' 0"`;
};

/**
 * Validate 6x6 post-size requirements per truss classification for a building.
 * Each structural classification has its own maximum span on 6x6 posts (Standard 50',
 * Class 1 16', 170 40', Monoslope 12'); spans beyond that recommend 8x8 posts. Returns a
 * classification-specific warning that names the offending truss so the user knows which
 * truss/limit applies. Header trusses carry no classification and default to Standard.
 *
 * Soft warning only — the user may acknowledge and proceed with 6x6.
 */
export const validatePostSizeForTrusses = (
  postSize: string,
  trusses: { gable: GableTruss[]; header: HeaderTruss[]; leanTo: LeanToTruss[] }
): { isValid: boolean; warningMessage: string } => {
  // Only 6x6 posts trigger warnings (8x8 does not).
  if (postSize !== '6x6') {
    return { isValid: true, warningMessage: '' };
  }

  const allTrusses: Array<GableTruss | HeaderTruss | LeanToTruss> = [
    ...trusses.gable,
    ...trusses.header,
    ...trusses.leanTo,
  ];

  for (const truss of allTrusses) {
    const totalSpanFeet = truss.sizeFeet + truss.sizeInches / 12;

    if (exceedsSixBySixLimit(truss.classification, totalSpanFeet)) {
      const classificationLabel = getSixBySixWarningLabel(truss.classification || 'Standard');
      const spanText = `${truss.sizeFeet}' ${truss.sizeInches}"`;
      return {
        isValid: false,
        warningMessage: `Truss US Engineering requires an 8x8 post for ${classificationLabel} trusses ${sixBySixLimitText(
          truss.classification
        )}. The selected ${classificationLabel} truss is ${spanText}.`,
      };
    }
  }

  return { isValid: true, warningMessage: '' };
};

/**
 * Map frontend display classification values to backend enum values.
 */
export const mapClassificationToBackend = (
  classification: string,
  _productType: 'Gable' | 'Header' | 'LeanTo'
): string => {
  const mapping: Record<string, string> = {
    Standard: 'Standard',
    'Class 1': 'Class1', // price-sheet display label → backend enum name
    Monoslope: 'Monoslope',
  };

  const normalized = normalizeLegacyClassification(classification);
  return mapping[normalized] ?? mapping[classification] ?? classification;
};

/**
 * Map backend enum classification values to frontend display values.
 */
export const mapClassificationToFrontend = (
  classification: string,
  _productType: 'Gable' | 'Header' | 'LeanTo'
): string => normalizeLegacyClassification(classification);

/**
 * Convert GableTruss to TrussRequest
 */
export const gableTrussToRequest = (truss: GableTruss, orderDate: string): TrussRequest => {
  return {
    id: truss.id,
    productType: 'Gable',
    classification: mapClassificationToBackend(truss.classification, 'Gable'),
    orderDate,
    sizeFeet: truss.sizeFeet,
    sizeInches: truss.sizeInches,
    quantity: truss.quantity,
    measurementInsidePost: truss.measurementInsidePost,
    confirmedMeasurementInsidePost: truss.confirmedMeasurementInsidePost,
    roofPitch: truss.roofPitch,
    overhangType: truss.overhangType,
    overhangSize: truss.overhangSize,
    carriageBolts: truss.carriageBolts,
    notes: truss.notes,
    price: truss.price,
  };
};

/**
 * Convert HeaderTruss to TrussRequest
 * Note: Header trusses don't have classification, so it's always an empty string
 */
export const headerTrussToRequest = (truss: HeaderTruss, orderDate: string): TrussRequest => {
  return {
    id: truss.id,
    productType: 'Header',
    classification: '', // Header trusses don't have classification
    orderDate,
    sizeFeet: truss.sizeFeet,
    sizeInches: truss.sizeInches,
    quantity: truss.quantity,
    measurementInsidePost: truss.measurementInsidePost,
    confirmedMeasurementInsidePost: truss.confirmedMeasurementInsidePost,
    carriageBolts: truss.carriageBolts,
    notes: truss.notes,
    price: truss.price,
  };
};

/**
 * Convert LeanToTruss to TrussRequest
 */
export const leanToTrussToRequest = (truss: LeanToTruss, orderDate: string): TrussRequest => {
  return {
    id: truss.id,
    productType: 'LeanTo',
    classification: mapClassificationToBackend(truss.classification, 'LeanTo'),
    orderDate,
    sizeFeet: truss.sizeFeet,
    sizeInches: truss.sizeInches,
    quantity: truss.quantity,
    measurementInsidePost: truss.measurementInsidePost,
    confirmedMeasurementInsidePost: truss.confirmedMeasurementInsidePost,
    roofPitch: truss.roofPitch,
    overhangType: truss.overhangType,
    overhangSize: truss.overhangSize,
    carriageBolts: truss.carriageBolts,
    notes: truss.notes,
    monoslopeTruss: truss.monoslopeTruss,
    postSizeOverride: truss.postSizeOverride,
    postSizeOverrideValue:
      truss.postSizeOverrideValue && truss.postSizeOverrideValue.trim()
        ? postSizeDisplayToEnum[truss.postSizeOverrideValue] || truss.postSizeOverrideValue
        : null,
    connectToHeaderTruss: truss.connectToHeaderTruss,
    price: truss.price,
  };
};

/**
 * Create a new empty truss request
 */
export const createEmptyTrussRequest = (
  productType: 'Gable' | 'Header' | 'LeanTo',
  orderDate: string,
  defaultClassification: string = 'Standard'
): TrussRequest => {
  const base: TrussRequest = {
    id: 0, // New truss
    productType,
    classification: defaultClassification,
    orderDate,
    sizeFeet: 14,
    sizeInches: 0,
    quantity: 1,
    measurementInsidePost: 0,
    confirmedMeasurementInsidePost: false,
    carriageBolts: false,
    price: 0,
  };

  if (productType === 'Gable') {
    return {
      ...base,
      roofPitch: '4:12',
      overhangType: 'Standard',
    };
  }

  if (productType === 'LeanTo') {
    return {
      ...base,
      roofPitch: '5:12',
      overhangType: 'Standard',
    };
  }

  return base;
};

/** Ensures each truss has a freshly calculated inside-post measurement before save/validation. */
export const withRecalculatedInsidePostMeasurements = (
  trusses: { gable: GableTruss[]; header: HeaderTruss[]; leanTo: LeanToTruss[] },
  buildingPostSize: string
): { gable: GableTruss[]; header: HeaderTruss[]; leanTo: LeanToTruss[] } => {
  const normalizedPostSize = resolveBuildingPostSizeDisplay(buildingPostSize);

  return {
    gable: trusses.gable.map(truss => ({
      ...truss,
      measurementInsidePost: resolveInsidePostMeasurementForTruss(
        truss.sizeFeet,
        truss.sizeInches,
        'Gable',
        normalizedPostSize
      ),
    })),
    header: trusses.header.map(truss => ({
      ...truss,
      measurementInsidePost: resolveInsidePostMeasurementForTruss(
        truss.sizeFeet,
        truss.sizeInches,
        'Header',
        normalizedPostSize
      ),
    })),
    leanTo: trusses.leanTo.map(truss => ({
      ...truss,
      measurementInsidePost: resolveInsidePostMeasurementForTruss(
        truss.sizeFeet,
        truss.sizeInches,
        'LeanTo',
        normalizedPostSize,
        {
          monoslopeTruss: truss.monoslopeTruss,
          postSizeOverride: truss.postSizeOverride,
          postSizeOverrideValue: truss.postSizeOverrideValue,
        }
      ),
    })),
  };
};

/**
 * Generate accordion header text for a truss
 */
export const getTrussAccordionHeader = (truss: GableTruss | HeaderTruss | LeanToTruss): string => {
  const sizeText = `${truss.sizeFeet}' ${truss.sizeInches}"`;
  const quantityText = `x ${truss.quantity}`;

  if (truss.productType === 'Header') {
    return `${sizeText} ${quantityText}`;
  }

  // For Gable and LeanTo, include classification
  const classificationText = truss.classification || 'Standard';
  return `${classificationText}, ${sizeText} ${quantityText}`;
};
